import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Users, Mail, CreditCard, Euro } from "lucide-react";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-3xl bg-white p-7">
    <Icon size={20} strokeWidth={1.5} className="text-sagedark" />
    <p className="mt-4 font-serif text-4xl text-ink">{value}</p>
    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-smoke">{label}</p>
  </div>
);

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [overview, setOverview] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [members, setMembers] = useState([]);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (user === false) navigate("/connexion");
    else if (user && user.role !== "admin") navigate("/espace-client");
  }, [user, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/overview").then(({ data }) => setOverview(data)).catch(() => {});
    api.get("/admin/contacts").then(({ data }) => setContacts(data)).catch(() => {});
    api.get("/admin/members").then(({ data }) => setMembers(data)).catch(() => {});
    api.get("/admin/bookings").then(({ data }) => setBookings(data)).catch(() => {});
  }, [user]);

  if (!user || user.role !== "admin") {
    return <div data-testid="admin-loading" className="min-h-screen bg-paper flex items-center justify-center"><p className="font-serif italic text-xl text-smoke">Chargement du back-office…</p></div>;
  }

  const markDone = async (id) => {
    await api.patch(`/admin/contacts/${id}`, { status: "traité" });
    setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, status: "traité" } : c)));
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-paper">
      <header className="border-b border-linen bg-white/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" aria-label="Accueil VAV"><Logo /></Link>
          <div className="flex items-center gap-5">
            <span className="text-xs uppercase tracking-[0.25em] text-sagedark">Back-office</span>
            <Link data-testid="admin-site-link" to="/" className="hidden md:inline text-sm text-smoke hover:text-ink transition-colors duration-300">Le site</Link>
            <button data-testid="admin-logout-button" onClick={async () => { await logout(); navigate("/"); }} className="inline-flex items-center gap-2 text-sm text-smoke hover:text-ink transition-colors duration-300">
              <LogOut size={16} strokeWidth={1.5} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-ink">Pilotage du cabinet</h1>

        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard testid="stat-members" icon={Users} label="Membres inscrits" value={overview?.members ?? "—"} />
          <StatCard testid="stat-contacts" icon={Mail} label="Demandes reçues" value={overview ? `${overview.contacts} (${overview.new_contacts} nouv.)` : "—"} />
          <StatCard testid="stat-subscriptions" icon={CreditCard} label="Abonnements actifs" value={overview?.subscriptions ?? "—"} />
          <StatCard testid="stat-revenue" icon={Euro} label="Revenu mensuel estimé" value={overview ? `${overview.monthly_revenue.toLocaleString("fr-FR")} €` : "—"} />
        </div>

        <div className="mt-12 flex rounded-full bg-bone p-1 w-fit" data-testid="admin-tabs">
          {[["contacts", "Demandes de contact"], ["members", "Membres"]].map(([t, label]) => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)} className={`rounded-full px-6 py-2.5 text-sm transition-colors duration-300 ${tab === t ? "bg-ink text-paper" : "text-smoke hover:text-ink"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "contacts" && (
          <div data-testid="admin-contacts-table" className="mt-8 rounded-3xl bg-white overflow-hidden">
            {contacts.length === 0 ? (
              <p className="p-10 text-sm text-smoke">Aucune demande pour le moment.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-smoke border-b border-linen">
                    <th className="px-6 py-4 font-medium">Date</th>
                    <th className="px-6 py-4 font-medium">Nom</th>
                    <th className="px-6 py-4 font-medium">Type</th>
                    <th className="px-6 py-4 font-medium hidden lg:table-cell">Message</th>
                    <th className="px-6 py-4 font-medium">Statut</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((c) => (
                    <tr key={c.id} data-testid={`admin-contact-row-${c.id}`} className="border-b border-linen/60 last:border-0">
                      <td className="px-6 py-4 text-smoke whitespace-nowrap">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                      <td className="px-6 py-4">
                        <span className="block text-ink">{c.name}</span>
                        <span className="block text-xs text-smoke">{c.email}</span>
                      </td>
                      <td className="px-6 py-4"><span className="rounded-full bg-bone px-3 py-1 text-xs">{c.kind}</span></td>
                      <td className="px-6 py-4 text-smoke max-w-xs truncate hidden lg:table-cell">{c.message}</td>
                      <td className="px-6 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs ${c.status === "nouveau" ? "bg-sage/15 text-sagedark" : "bg-bone text-smoke"}`}>{c.status}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {c.status === "nouveau" && (
                          <button data-testid={`admin-contact-done-${c.id}`} onClick={() => markDone(c.id)} className="text-xs rounded-full border border-ink/25 px-4 py-2 hover:bg-ink hover:text-paper transition-colors duration-300">
                            Marquer traité
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <div data-testid="admin-bookings-table" className="mt-8 rounded-3xl bg-white overflow-hidden">
            {bookings.length === 0 ? (
              <p className="p-10 text-sm text-smoke">Aucun rendez-vous de coaching planifié.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-smoke border-b border-linen">
                    <th className="px-6 py-4 font-medium">Créneau</th>
                    <th className="px-6 py-4 font-medium">Membre</th>
                    <th className="px-6 py-4 font-medium hidden lg:table-cell">Sujet</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="border-b border-linen/60 last:border-0">
                      <td className="px-6 py-4 text-ink whitespace-nowrap capitalize">
                        {new Date(`${b.date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })} · {b.time}
                      </td>
                      <td className="px-6 py-4">
                        <span className="block text-ink">{b.user_name}</span>
                        <span className="block text-xs text-smoke">{b.user_email}</span>
                      </td>
                      <td className="px-6 py-4 text-smoke max-w-xs truncate hidden lg:table-cell">{b.topic}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === "members" && (
          <div data-testid="admin-members-table" className="mt-8 rounded-3xl bg-white overflow-hidden">
            <div className="flex items-center justify-between px-6 pt-5">
              <p className="text-xs uppercase tracking-[0.2em] text-smoke">{members.length} membre{members.length > 1 ? "s" : ""}</p>
              <a
                data-testid="admin-export-csv-button"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/members.csv`}
                className="text-xs rounded-full border border-ink/25 px-4 py-2 text-ink hover:bg-ink hover:text-paper transition-colors duration-300"
              >
                Exporter en CSV
              </a>
            </div>
            {members.length === 0 ? (
              <p className="p-10 text-sm text-smoke">Aucun membre inscrit pour le moment.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-[0.18em] text-smoke border-b border-linen">
                    <th className="px-6 py-4 font-medium">Membre</th>
                    <th className="px-6 py-4 font-medium">Module</th>
                    <th className="px-6 py-4 font-medium">Inscription</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id} data-testid={`admin-member-row-${m.id}`} className="border-b border-linen/60 last:border-0">
                      <td className="px-6 py-4">
                        <span className="block text-ink">{m.name}</span>
                        <span className="block text-xs text-smoke">{m.email}</span>
                      </td>
                      <td className="px-6 py-4">
                        {m.module ? (
                          <span className="rounded-full bg-sage/15 text-sagedark px-3 py-1 text-xs">{String(m.module_id).padStart(2, "0")} — {m.module}</span>
                        ) : (
                          <span className="text-xs text-smoke">Aucun module</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-smoke">{m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
